<template>
  <div class="main-page">
    <div class="main-header">智慧停车场</div>
    <div class="select-lot" v-if="parkingOptionsCount <= 1">
      <input
        ref="lotSearchInput"
        :value="selectedLabel"
        class="lot-input"
        type="text"
        readonly
        autocomplete="off"
      />
    </div>
    <div class="select-lot" v-else @click="toggleDropdown">
      <input
        ref="lotSearchInput"
        :value="selectedLabel"
        class="lot-input"
        type="text"
        readonly
        autocomplete="off"
      />
      <div class="dropdown-panel" v-if="showDropdown">
        <div class="dropdown-list">
          <div
            v-for="opt in parkingOptions"
            :key="opt.value"
            class="dropdown-item"
            @click.stop="selectOption(opt)"
          >
            <span class="tag parking">停车场</span>
            <span class="label">{{ opt.label }}</span>
          </div>
          <div v-if="parkingOptions.length === 0" class="dropdown-empty">暂无停车场</div>
        </div>
      </div>
    </div>
    <!-- 商家月卡套餐下拉选择框 -->
    <div class="select-package" v-if="currentSelectedOption && currentSelectedOption.type === 'parking' && (displayedMerchantPackages && displayedMerchantPackages.length > 0 || merchantPackagesLoading)">
      <input
        ref="packageSearchInput"
        :value="selectedPackageLabel"
        class="package-input"
        type="text"
        readonly
        autocomplete="off"
        @click="togglePackageDropdown"
      />
      <div class="package-dropdown-panel" v-if="showPackageDropdown">
        <div class="dropdown-list" ref="packageDropdownList" @scroll="handlePackageListScroll">
          <div
            v-for="pkg in displayedMerchantPackages"
            :key="pkg.productId || pkg.id"
            class="dropdown-item"
            @click="selectPackage(pkg)"
          >
            <span class="label">{{ pkg.productName || pkg.name || '月卡套餐' }}</span>
          </div>
          <div v-if="merchantPackagesLoading" class="dropdown-loading">加载中...</div>
          <div v-if="!merchantPackagesLoading && displayedMerchantPackages.length === 0" class="dropdown-empty">暂无套餐</div>
          <div v-if="!merchantPackagesLoading && hasMoreMerchantPackages && displayedMerchantPackages.length > 0" class="dropdown-load-more">滚动加载更多</div>
        </div>
      </div>
    </div>
    <div class="package-card" v-if="selectedMerchantPackage">
      <div class="package-title">{{ selectedMerchantPackage.productName || selectedMerchantPackage.name || '月卡套餐' }}</div>
      <div class="package-price" v-if="displaySelectedPkgPrice">{{ displaySelectedPkgPrice }}</div>
      <button class="buy-btn" @click="openOrder">立即购买</button>
    </div>
    <div class="package-card" v-else-if="currentSelectedOption && currentSelectedOption.type === 'parking'">
      <div class="no-package-text">请选择月卡套餐</div>
    </div>
    <div class="package-card" v-else>
      <div class="no-package-text">暂无月卡套餐</div>
    </div>
    <div class="owned-title">已购套餐</div>
    <div v-if="!ownedPackages || ownedPackages.length === 0" class="owned-card">
      <div class="pkg">暂未购买</div>
    </div>
    <div
      v-else
      v-for="(pkg, idx) in ownedPackages"
      :key="pkg.orderId || pkg.productId || pkg.id || idx"
      class="owned-card"
    >
      <div class="pkg">{{ pkg.productName || '月卡套餐' }}</div>
      <div class="valid" v-if="pkg.cardValidStartTime || pkg.cardValidEndTime">
        有效期：{{ formatDate(pkg.cardValidStartTime) }}至{{ formatDate(pkg.cardValidEndTime) }}
      </div>
      <div class="info">
        车位数量：{{ pkg.bindBerthNumbers || pkg.parkingSpace || '-' }}
        &nbsp;&nbsp;&nbsp;已绑定车牌：{{ getOrderBindPlateCount(pkg) }}
      </div>
    </div>
    <div v-if="shouldShowLoadMoreButton" class="load-more-container">
      <button class="load-more-btn" @click="loadMoreOwnedPackages">加载更多</button>
    </div>
    <div v-if="shouldShowNoMoreText" class="load-more-container">
      <span class="no-more-text">没有更多了</span>
    </div>
    <div v-if="ownedPackagesLoading" class="load-more-loading">加载中...</div>
    <div class="section-title">车辆管理</div>
    <div class="plate-list-card">
      <div class="inlot-bar">
        <span>在场车辆</span>
        <span>{{ inParkVehiclesTotal }}<template v-if="totalOrderBerths != null">/{{ totalOrderBerths }}</template></span>
      </div>
      <div class="plate-list">
        <div class="plate-row" v-for="p in displayedPlates" :key="p.id || p.num || p">
          <span class="plate-icon">🚗</span>
          <div class="plate-info-wrapper">
            <span class="plate-number">{{ p.num || p }}</span>
            <span class="plate-time" v-if="p.time">入场时间：{{ p.time }}</span>
          </div>
          <span><span class="plate-status">在场</span><span class="arrow">&#8250;</span></span>
        </div>
      </div>
      <div v-if="shouldShowLoadMoreInParkVehicles" class="load-more-container">
        <button class="load-more-btn" @click="loadMoreInParkVehicles">加载更多</button>
      </div>
      <div v-if="shouldShowNoMoreInParkVehicles" class="load-more-container">
        <span class="no-more-text">没有更多了</span>
      </div>
      <div v-if="inParkVehiclesLoading" class="load-more-loading">加载中...</div>
    </div>
    <button class="manage-btn" @click="goVehicle">管理车牌</button>
    <BottomNav />
  </div>
</template>

<script>
import BottomNav from '@/components/BottomNav.vue'
import request from '@/utils/request'
import { getBusinessByUser } from '@/api/business'
// 移除未使用的 getUserInfo，改为从本地缓存读取 userId
export default {
  name: 'HomePage',
  components: { BottomNav },
  data () {
    return {
      plates: [],
      plateRawRows: [],
      plateDetails: [],
      totalBerths: null,
      // 下拉相关
      showDropdown: false,
      searchKeyword: '',
      selectedLabel: '',
      loading: false,
      options: [] // { type: 'parking'|'road', value: id, label: name-address }
      ,currentSelectedOption: null
      ,packages: []
      ,ownedPackages: []
      ,merchants: []
      ,currentBusinessId: null
      ,businessContext: null
      ,businessContextPromise: null
      ,debugEnabled: true
      ,plateToOrderMap: null // 车牌号到月卡订单的映射
      ,merchantPackages: [] // 商家月卡套餐列表（所有已加载的）
      ,displayedMerchantPackages: [] // 当前显示的产品列表（根据停车场筛选）
      ,selectedMerchantPackage: null // 当前选择的商家月卡套餐
      ,selectedMerchantPackageDetail: null // 当前选择的商家月卡套餐详情
      ,showPackageDropdown: false // 套餐下拉框显示状态
      ,merchantPackagesPage: 1 // 当前页码
      ,merchantPackagesPageSize: 10 // 每页大小
      ,merchantPackagesTotal: 0 // 总记录数
      ,merchantPackagesLoading: false // 是否正在加载
      ,hasMoreMerchantPackages: false // 是否还有更多数据
      ,ownedPackagesPage: 1 // 已购套餐当前页码
      ,ownedPackagesPageSize: 5 // 已购套餐每页大小
      ,ownedPackagesTotal: 0 // 已购套餐总记录数
      ,ownedPackagesLoading: false // 已购套餐是否正在加载
      ,hasMoreOwnedPackages: false // 已购套餐是否还有更多数据
      ,inParkVehiclesPage: 1 // 在场车辆当前页码
      ,inParkVehiclesPageSize: 5 // 在场车辆每页大小
      ,inParkVehiclesTotal: 0 // 在场车辆总记录数
      ,inParkVehiclesLoading: false // 在场车辆是否正在加载
      ,hasMoreInParkVehicles: false // 在场车辆是否还有更多数据
      ,inParkVehiclesAll: [] // 在场车辆全量数据（后端不分页，前端做分页展示）
      ,totalOrderPlateNumbers: null // 所有商家月卡订单中的车牌总数（去重后）
      ,totalOrderPlateNumbersLoading: false // 是否正在加载车牌总数
    }
  },
  computed: {
    displayPkgTitle () {
      // 若已购套餐存在，取第一条名称；否则用默认文案
      const first = Array.isArray(this.packages) && this.packages.length > 0 ? this.packages[0] : null
      return (first && (first.productName || first.name)) || '月卡套餐A'
    },
    selectedPackageLabel () {
      if (this.selectedMerchantPackage) {
        return this.selectedMerchantPackage.productName || this.selectedMerchantPackage.name || '月卡套餐'
      }
      return '请选择月卡套餐'
    },
    displaySelectedPkgPrice () {
      if (!this.selectedMerchantPackage) return ''
      // 优先使用详情信息中的价格，如果没有详情信息则使用下拉列表中的信息
      const pkg = this.selectedMerchantPackageDetail || this.selectedMerchantPackage
      // 兼容多种价格字段：monthPrice/price/amount/fee
      const raw = pkg.monthPrice ?? pkg.price ?? pkg.amount ?? pkg.fee
      if (raw == null) return ''
      const n = Number(raw)
      
      // 根据cardType确定单位
      let unit = '月'
      const cardType = pkg.cardType || pkg.productType || pkg.type
      if (cardType) {
        const typeStr = String(cardType).toUpperCase()
        if (typeStr.includes('DAY') || typeStr.includes('DAILY') || typeStr.includes('日')) {
          unit = '日'
        } else if (typeStr.includes('YEAR') || typeStr.includes('YEARLY') || typeStr.includes('年')) {
          unit = '年'
        } else if (typeStr.includes('MONTH') || typeStr.includes('MONTHLY') || typeStr.includes('月')) {
          unit = '月'
        }
      }
      
      // 获取validityDuration
      const validityDuration = pkg.validityDuration || pkg.validityMonths || pkg.validityDays || pkg.validityYears
      
      // 构建单位字符串，如："30日"、"1月"、"1年"
      const unitStr = validityDuration ? `${validityDuration}${unit}` : unit
      
      const val = isNaN(n) ? String(raw) : `¥${n}/${unitStr}`
      return val
    },
    displayPkgPrice () {
      const first = Array.isArray(this.packages) && this.packages.length > 0 ? this.packages[0] : null
      if (!first) return '¥299/月'
      // 兼容多种价格字段：monthPrice/price/amount/fee
      const raw = first.monthPrice ?? first.price ?? first.amount ?? first.fee
      if (raw == null) return ''
      const n = Number(raw)
      
      // 根据cardType确定单位
      let unit = '月'
      const cardType = first.cardType || first.productType || first.type
      if (cardType) {
        const typeStr = String(cardType).toUpperCase()
        if (typeStr.includes('DAY') || typeStr.includes('DAILY') || typeStr.includes('日')) {
          unit = '日'
        } else if (typeStr.includes('YEAR') || typeStr.includes('YEARLY') || typeStr.includes('年')) {
          unit = '年'
        } else if (typeStr.includes('MONTH') || typeStr.includes('MONTHLY') || typeStr.includes('月')) {
          unit = '月'
        }
      }
      
      // 获取validityDuration
      const validityDuration = first.validityDuration || first.validityMonths || first.validityDays || first.validityYears
      
      // 构建单位字符串，如："30日"、"1月"、"1年"
      const unitStr = validityDuration ? `${validityDuration}${unit}` : unit
      
      const val = isNaN(n) ? String(raw) : `¥${n}/${unitStr}`
      return val
    },
    filteredOptions () {
      const kw = (this.searchKeyword || '').trim().toLowerCase()
      if (!kw) return this.options
      return this.options.filter(o => o.label.toLowerCase().includes(kw))
    },
    parkingOptions () {
      // 只返回停车场类型的选项
      return Array.isArray(this.options) ? this.options.filter(opt => opt.type === 'parking') : []
    },
    parkingOptionsCount () {
      // 停车场选项的数量
      return this.parkingOptions.length
    },
    displayedPlates () {
      // 显示所有车辆信息，通过滚动查看
      // 优先使用 plateDetails（包含时间信息），如果没有则回退到 plates
      if (Array.isArray(this.plateDetails) && this.plateDetails.length > 0) {
        return this.plateDetails
      }
      // 如果 plateDetails 为空，将 plates 转换为对象格式以保持兼容
      return Array.isArray(this.plates) ? this.plates.map(p => ({ num: p, time: '' })) : []
    },
    // 检查"加载更多"按钮是否应该显示
    shouldShowLoadMoreButton () {
      const shouldShow = this.hasMoreOwnedPackages && !this.ownedPackagesLoading
      console.log('[已购套餐] shouldShowLoadMoreButton: 计算按钮显示状态:', {
        hasMoreOwnedPackages: this.hasMoreOwnedPackages,
        ownedPackagesLoading: this.ownedPackagesLoading,
        shouldShow,
        calculation: `${this.hasMoreOwnedPackages} && !${this.ownedPackagesLoading} = ${shouldShow}`,
        ownedPackagesPage: this.ownedPackagesPage,
        ownedPackagesTotal: this.ownedPackagesTotal,
        ownedPackagesLength: this.ownedPackages.length
      })
      return shouldShow
    },
    // 检查是否显示"没有更多了"文本
    shouldShowNoMoreText () {
      // 只有在不显示"加载更多"按钮、不在加载中、且已加载数据的情况下才显示"没有更多了"
      return !this.hasMoreOwnedPackages && !this.ownedPackagesLoading && this.ownedPackages.length > 0
    },
    // 检查在场车辆"加载更多"按钮是否应该显示
    shouldShowLoadMoreInParkVehicles () {
      return this.hasMoreInParkVehicles && !this.inParkVehiclesLoading
    },
    // 检查是否显示在场车辆"没有更多了"文本
    shouldShowNoMoreInParkVehicles () {
      return !this.hasMoreInParkVehicles && !this.inParkVehiclesLoading && this.plateDetails.length > 0
    },
    // 计算月卡订单绑定的车牌数总和（去重）
    // 优先使用接口查询到的总数，如果没有则使用已加载订单的统计
    totalOrderBerths () {
      // 如果已经通过接口查询到了总数，优先使用
      if (this.totalOrderPlateNumbers != null && this.totalOrderPlateNumbers !== undefined) {
        return this.totalOrderPlateNumbers > 0 ? this.totalOrderPlateNumbers : null
      }
      
      // 否则，使用已加载订单的统计（兼容旧逻辑）
      if (!Array.isArray(this.ownedPackages) || this.ownedPackages.length === 0) {
        return null
      }
      // 收集所有订单中绑定的车牌号（去重）
      const plateSet = new Set()
      this.ownedPackages.forEach(pkg => {
        const order = pkg._order
        if (order) {
          const plates = order?.plateNumbers || 
                         order?.plateNumberList || 
                         order?.plateList || 
                         order?.plates || []
          if (Array.isArray(plates)) {
            // 过滤掉空字符串、null、undefined等无效值，并标准化车牌号
            plates.forEach(p => {
              if (p != null) {
                const str = String(p).trim().toUpperCase()
                if (str.length > 0) {
                  plateSet.add(str)
                }
              }
            })
          }
        }
      })
      const total = plateSet.size
      // 如果总和为0，返回null（不显示）
      return total > 0 ? total : null
    }
  },
  async mounted () {
    // 页面加载前检查商户状态
    await this.checkBusinessStatus()
    this.fetchScopeOptions()
    // 添加点击外部关闭套餐下拉框的事件监听
    document.addEventListener('click', this.handlePackageOutsideClick)
  },
  async activated () {
    // 页面激活前检查商户状态
    await this.checkBusinessStatus()
    // 如果使用了 keep-alive，页面激活时重新加载数据
    // 保存当前选择的套餐信息
    const savedPackage = this.selectedMerchantPackage
    const savedPackageId = savedPackage ? (savedPackage.productId || savedPackage.id) : null
    
    // 清空已购套餐和在场车辆，避免显示旧数据
    this.ownedPackages = []
    this.plateDetails = []
    this.plates = []
    // 重置在场车辆分页状态
    this.inParkVehiclesPage = 1
    this.inParkVehiclesTotal = 0
    this.hasMoreInParkVehicles = false
    // 重置套餐分页状态
    this.merchantPackagesPage = 1
    this.merchantPackages = []
    this.displayedMerchantPackages = []
    this.merchantPackagesTotal = 0
    this.hasMoreMerchantPackages = false
    // 重置已购套餐分页状态
    console.log('[已购套餐] activated: 重置分页状态前的状态:', {
      ownedPackagesPage: this.ownedPackagesPage,
      ownedPackagesTotal: this.ownedPackagesTotal,
      hasMoreOwnedPackages: this.hasMoreOwnedPackages,
      ownedPackagesLength: this.ownedPackages.length,
      ownedPackagesLoading: this.ownedPackagesLoading
    })
    this.ownedPackagesPage = 1
    this.ownedPackagesTotal = 0
    this.hasMoreOwnedPackages = false
    console.log('[已购套餐] activated: 重置分页状态后的状态:', {
      ownedPackagesPage: this.ownedPackagesPage,
      ownedPackagesTotal: this.ownedPackagesTotal,
      hasMoreOwnedPackages: this.hasMoreOwnedPackages
    })
    
    await this.fetchScopeOptions()
    
    // 等待套餐列表加载完成（通过轮询检查）
    const maxWaitTime = 3000 // 最多等待3秒
    const checkInterval = 100 // 每100ms检查一次
    let waitedTime = 0
    
    while (waitedTime < maxWaitTime) {
      if (this.displayedMerchantPackages && this.displayedMerchantPackages.length > 0) {
        // 套餐列表已加载
        break
      }
      await new Promise(resolve => setTimeout(resolve, checkInterval))
      waitedTime += checkInterval
    }
    
    // 如果之前有选择的套餐，尝试恢复选择
    if (savedPackageId && this.displayedMerchantPackages && this.displayedMerchantPackages.length > 0) {
      // 查找之前选择的套餐
      const foundPackage = this.displayedMerchantPackages.find(pkg => {
        const pkgId = pkg.productId || pkg.id
        return String(pkgId) === String(savedPackageId)
      })
      if (foundPackage) {
        // 恢复套餐选择（这会重新加载已购套餐和在场车辆）
        this.selectPackage(foundPackage)
      } else if (this.displayedMerchantPackages.length > 0) {
        // 如果找不到之前的套餐，选择第一个
        this.selectPackage(this.displayedMerchantPackages[0])
      }
    } else if (this.displayedMerchantPackages && this.displayedMerchantPackages.length > 0 && !this.selectedMerchantPackage) {
      // 如果没有保存的套餐选择，但有套餐列表，选择第一个
      // 注意：fetchMerchantPackagesByParking 应该已经选择了第一个，但这里作为兜底
      if (!this.selectedMerchantPackage) {
        this.selectPackage(this.displayedMerchantPackages[0])
      }
    }
  },
  beforeUnmount () {
    // 移除事件监听
    document.removeEventListener('click', this.handlePackageOutsideClick)
  },
  methods: {
    async checkBusinessStatus () {
      // 检查商户是否被停用
      try {
        // 获取用户ID
        let userId = localStorage.getItem('userId')
        if (!userId) {
          const token = localStorage.getItem('token')
          if (token) {
            try {
              const parts = token.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                userId = payload.userId || payload.uid || payload.sub || null
              }
            } catch (e) { /* ignore */ }
          }
        }
        
        if (!userId) {
          console.warn('[Home] 无法获取用户ID，跳过商户状态检查')
          return
        }
        
        console.log('[Home] 检查商户状态，用户ID:', userId)
        
        // 获取商户信息
        const businessResp = await getBusinessByUser(userId)
        const business = businessResp?.data || businessResp
        
        console.log('[Home] 商户信息:', business)
        
        // 检查商户是否被停用
        if (business && (business.isDisabled === true || business.is_disabled === true)) {
          console.error('[Home] 商户已被停用，阻止访问页面')
          
          // 清除token和用户信息
          this.$store.commit('CLEAR_USER')
          this.$store.commit('SET_TOKEN', '')
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('userId')
          sessionStorage.removeItem('wx_open_id')
          
          // 提示用户并跳转到登录页
          alert('您的账号对应的商户已被停用，无法访问。请联系管理员。')
          this.$router.replace('/login')
          return
        }
        
        console.log('[Home] 商户状态正常')
      } catch (e) {
        console.warn('[Home] 检查商户状态失败（不影响页面加载）:', e)
        // 如果检查失败，为了安全起见，可以选择阻止访问或继续
        // 这里选择继续，因为可能是网络问题等临时错误
      }
    },
    openOrder () {
      try {
        // 优先使用selectedMerchantPackageDetail（产品详情），如果没有详情则使用selectedMerchantPackage，最后回退到packages
        const pkg = this.selectedMerchantPackageDetail || this.selectedMerchantPackage || (Array.isArray(this.packages) && this.packages.length > 0 ? this.packages[0] : null)
        if (pkg) {
          // 优先从详情中获取价格，如果没有详情则从下拉列表数据中获取
          const price = pkg.monthPrice ?? pkg.price ?? pkg.amount ?? pkg.fee
          const productId = pkg.productId || pkg.id
          const productName = pkg.productName || pkg.name || '月卡套餐'
          
          const payload = {
            id: productId,
            name: productName,
            price,
            businessId: this.currentBusinessId,
            // 使用详情信息作为raw，如果没有详情则使用下拉列表数据
            raw: this.selectedMerchantPackageDetail || pkg,
            parking: (() => {
              if (this.currentSelectedOption && this.currentSelectedOption.type === 'parking') {
                const sel = this.currentSelectedOption
                const name = (sel.label && sel.label.split(' - ')[0]) || ''
                const address = (sel.label && sel.label.split(' - ')[1]) || (sel.meta && (sel.meta.parkingAddress || sel.meta.address)) || ''
                return { id: sel.value, name, address }
              }
              return null
            })()
          }
          
          console.log('保存到sessionStorage的套餐信息:', payload)
          sessionStorage.setItem('selected_package', JSON.stringify(payload))
        } else {
          console.warn('openOrder: 没有可用的套餐信息')
          sessionStorage.removeItem('selected_package')
        }
      } catch (e) {
        console.error('openOrder: 保存套餐信息失败', e)
      }
      this.$router.push('/order')
    },
    async goVehicle () {
      try {
        const needRefresh = !Array.isArray(this.plateDetails) || this.plateDetails.length === 0
        
        if (needRefresh) {
          const opt = this.currentSelectedOption
          if (opt && opt.type === 'parking') {
            const name = (opt.label && opt.label.split(' - ')[0]) || ''
            await this.fetchInParkByOrder({ parkingId: opt.value, parkingName: name })
          } else if (this.currentBusinessId) {
            await this.fetchInParkByOrder({})
          }
        }
        // 注意：新接口直接返回在场车辆，不再需要从订单中建立映射关系
      } catch (e) {
        if (this.shouldDebug()) {
          // eslint-disable-next-line no-console
          console.warn('[Home] 进入车辆页面前刷新在场列表失败', e)
        }
      }
      try {
        const data = {
          plates: Array.isArray(this.plates) ? [...this.plates] : [],
          plateDetails: Array.isArray(this.plateDetails) ? [...this.plateDetails] : [],
          plateStatusRows: Array.isArray(this.plateDetails) ? [...this.plateDetails] : [],
          totalBerths: this.totalBerths,
          inParkRows: Array.isArray(this.plateRawRows) ? [...this.plateRawRows] : [],
          businessId: this.currentBusinessId,
          businessContext: this.businessContext,
          // 存入车牌号到月卡订单的映射关系
          plateToOrderMap: this.plateToOrderMap || {}
        }
        // 透传当前选择的停车场，供车辆页面与新增页默认带出
        if (this.currentSelectedOption && this.currentSelectedOption.type === 'parking') {
          const sel = this.currentSelectedOption
          const name = (sel.label && sel.label.split(' - ')[0]) || ''
          data.selectedParking = {
            id: sel.value,
            parkingId: sel.value,
            name,
            parkingName: name
          }
        }
        sessionStorage.setItem('in_park_plates_payload', JSON.stringify(data))
        this.debugLog('goVehicle:stored-payload', { 
          plateCount: data.plateDetails.length,
          orderMapSize: Object.keys(data.plateToOrderMap || {}).length 
        })
      } catch (e) { if (this.shouldDebug()) { /* eslint-disable no-console */ console.warn('store plates payload failed', e) } }
      this.$router.push('/vehicle')
    },
    async fetchScopeOptions () {
      this.loading = true
      
      try {
        // 不调用接口：从本地缓存读取 userId；必要时从 token 解析
        let userId = localStorage.getItem('userId')
        if (!userId) {
          const token = localStorage.getItem('token')
          if (token) {
            try {
              const parts = token.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                userId = payload.userId || payload.uid || payload.sub
              }
            } catch (e) { /* ignore */ }
          }
        }
        if (!userId) {
          console.warn('未能从本地获取userId，无法查询商户列表')
          this.loading = false
          return
        }

        // 根据用户ID查询该用户作为管理员的商户，提取商户绑定的停车场/道路
        let merchantList = []
        if (userId) {
          try {
            const merchantRes = await request({
              url: '/market/business/list',
              method: 'post',
              data: { administor: userId },
              timeout: 30000
            })
            merchantList = merchantRes?.data || []
            // 保存商户信息与默认 businessId
            this.merchants = merchantList
            this.currentBusinessId = (merchantList && merchantList[0] && merchantList[0].businessId) || null
            this.businessContext = null
            this.debugLog('fetchScopeOptions:merchant-list', { userId, count: merchantList?.length, merchantList })
            console.log('查询到商户列表:', merchantList)
          } catch (e) {
            console.warn('查询商户列表失败:', e)
            // 如果查询商户列表失败，保留原有选项，不更新
            this.loading = false
            return
          }
        } else {
          // 如果没有用户ID，保留原有选项，不更新
          console.warn('未获取到用户ID，无法查询商户列表')
          this.loading = false
          return
        }
        
        // 获取所有商户的停车场ID和道路ID
        const allParkIds = []
        const allRoadIds = []
        merchantList.forEach(m => {
          if (m && Array.isArray(m.parkIds)) allParkIds.push(...m.parkIds)
          if (m && Array.isArray(m.roadIds)) allRoadIds.push(...m.roadIds)
        })

        this.debugLog('fetchScopeOptions:park-road-ids', { parkCount: allParkIds.length, roadCount: allRoadIds.length, parkIds: allParkIds, roadIds: allRoadIds })
        console.log('商户关联的停车场ID列表:', allParkIds)
        console.log('商户关联的道路ID列表:', allRoadIds)

        // 使用后端批量查询接口，根据商户关联的ID列表查询（筛选逻辑在后端）
        let parkings = []
        if (allParkIds.length > 0) {
          try {
            // 去重
            const uniqueParkIds = [...new Set(allParkIds)]
            const parkingRes = await request({ 
              url: '/parking/info/list/by-ids', 
              method: 'post', 
              data: uniqueParkIds,
              timeout: 30000
            })
            parkings = (parkingRes && parkingRes.data) || []
            this.debugLog('fetchScopeOptions:parking-list', { requestIds: uniqueParkIds, count: parkings?.length, parkings })
            console.log('根据ID列表查询到停车场:', parkings.length)
          } catch (parkingError) {
            console.warn('查询停车场失败:', parkingError)
          }
        } else {
          // 如果没有商户关联的停车场ID，不查询所有停车场，返回空列表
          console.log('没有商户关联的停车场ID，返回空列表')
          parkings = []
        }

        // 使用后端批量查询接口，根据商户关联的道路ID列表查询
        let roads = []
        if (allRoadIds.length > 0) {
          try {
            // 去重
            const uniqueRoadIds = [...new Set(allRoadIds)]
            const roadRes = await request({ 
              url: '/road/list/by-ids', 
              method: 'post', 
              data: uniqueRoadIds,
              timeout: 30000
            })
            roads = (roadRes && roadRes.data) || []
            this.debugLog('fetchScopeOptions:road-list', { requestIds: uniqueRoadIds, count: roads?.length, roads })
            console.log('根据ID列表查询到道路:', roads.length)
          } catch (roadError) {
            console.warn('查询道路失败:', roadError)
          }
        } else {
          // 如果没有商户关联的道路ID，不查询，返回空列表
          console.log('没有商户关联的道路ID，返回空列表')
          roads = []
        }

        const parkingOptions = parkings.map(p => ({
          type: 'parking',
          value: p.parkingId || p.id || p.parkingCode,
          label: [p.parkingName, p.parkingAddress].filter(Boolean).join(' - '),
          meta: p
        }))
        const roadOptions = roads.map(r => ({
          type: 'road',
          value: r.roadId || r.id,
          label: [r.roadName, r.roadAddress].filter(Boolean).join(' - ')
        }))

        // 成功获取数据后，更新选项列表
        this.options = [...parkingOptions, ...roadOptions]
        
        // 如果获取到新的选项，清空当前选择以便重新选择
        if (this.options.length > 0) {
          // 只有当有新的选项时才清空当前选择
          this.currentSelectedOption = null
          this.selectedLabel = ''
          this.searchKeyword = ''
        }
        
        // 默认选中首个停车场
        if (parkingOptions.length > 0) {
          this.debugLog('fetchScopeOptions:auto-select-parking', { option: parkingOptions[0] })
          this.selectOption(parkingOptions[0])
        } else {
          // 如果没有停车场，清空套餐相关数据
          this.packages = []
          this.ownedPackages = []
          this.merchantPackages = []
          this.displayedMerchantPackages = []
          this.merchantPackagesPage = 1
          this.merchantPackagesTotal = 0
          this.hasMoreMerchantPackages = false
          this.selectedMerchantPackage = null
          this.selectedMerchantPackageDetail = null
        }
        
        console.log('成功加载选项:', { 
          merchantCount: merchantList.length,
          parkingCount: parkings.length, 
          roadCount: roads.length,
          totalOptions: this.options.length
        })
      } catch (e) {
        console.error('加载选项失败:', e)
        // 发生错误时，保留原有选项，不更新
        // 这样可以避免在页面切换时因为临时错误而显示"无匹配结果"
      } finally {
        this.loading = false
      }
    },
    toggleDropdown (e) {
      // 仅在点击容器时切换，点击内部控件由各自事件控制
      if (e && (e.target && (e.target.tagName === 'INPUT' || e.target.classList.contains('clear-btn')))) return
      this.showDropdown = !this.showDropdown
      if (this.showDropdown) this.$nextTick(() => this.$refs.lotSearchInput && this.$refs.lotSearchInput.focus())
    },
    openDropdown () {
      this.showDropdown = true
    },
    handleOutsideClick (evt) {
      const root = this.$el.querySelector('.select-lot')
      if (root && !root.contains(evt.target)) {
        this.showDropdown = false
      }
    },
    onSearchInput () {
      // 即时过滤由 computed 处理，这里保持方法存在以便未来扩展
      return undefined
    },
    clearSearch () {
      this.searchKeyword = ''
    },
    selectOption (opt) {
      this.selectedLabel = opt.label
      this.currentSelectedOption = opt
      this.showDropdown = false
      this.searchKeyword = ''
      // 清空套餐选择
      this.selectedMerchantPackage = null
      this.merchantPackages = []
      this.displayedMerchantPackages = []
      this.merchantPackagesPage = 1
      this.merchantPackagesTotal = 0
      this.hasMoreMerchantPackages = false
      if (opt && opt.type === 'parking') {
        // 优先从选项元数据读取总泊位，兼容多字段名
        if (opt.meta) {
          const m = opt.meta
          const total = m.totalBerths ?? m.totalParkingSpace ?? m.totalBerth ?? m.total ?? m.berthCount ?? m.totalBerthNumbers ?? m.actualBerths
          this.totalBerths = (typeof total === 'number') ? total : (Number(total) || null)
        } else {
          this.totalBerths = null
        }
        // 清空套餐选择，等待套餐列表加载后再选择
        this.selectedMerchantPackage = null
        this.ownedPackages = []
        this.plateDetails = []
        // 重置分页状态
        this.merchantPackagesPage = 1
        this.inParkVehiclesPage = 1
        this.inParkVehiclesTotal = 0
        this.hasMoreInParkVehicles = false
        this.merchantPackages = []
        this.displayedMerchantPackages = []
        // 加载该停车场的商家月卡套餐列表（会自动选择第一个套餐，并加载已购套餐和在场车辆）
        if (this.currentBusinessId) {
          this.fetchMerchantPackagesByParking(opt.value, this.currentBusinessId, 1, false)
        }
        // 回退策略：若未拿到总泊位，再查询一次详情
        if (this.totalBerths == null) this.fetchParkingInfoById(opt.value)
        // 注意：不再在这里调用 fetchInParkByOrder，因为 fetchMerchantPackagesByParking 会调用 selectPackage，
        // 而 selectPackage 会调用 fetchInParkByOrder（此时已选择了套餐）
      } else if (opt && opt.type === 'road') {
        if (this.currentBusinessId) this.fetchPackagesByBusiness(this.currentBusinessId)
        if (this.currentBusinessId) {
          this.ownedPackagesPage = 1
          this.ownedPackages = []
          this.fetchOwnedPackagesByBusiness(this.currentBusinessId, null, 1, false)
        }
        this.plates = []
        this.totalBerths = null
      } else {
        this.packages = []
        this.ownedPackages = []
        this.plates = []
        this.totalBerths = null
      }
    },
    clearSelection () {
      this.selectedLabel = ''
      this.currentSelectedOption = null
      this.searchKeyword = ''
      // 清空选择后的展示内容
      this.packages = []
      this.ownedPackages = []
      this.plates = []
      this.plateDetails = []
      this.totalBerths = null
      this.showDropdown = false
      // 清空套餐选择
      this.selectedMerchantPackage = null
      this.selectedMerchantPackageDetail = null
      this.merchantPackages = []
      this.displayedMerchantPackages = []
      this.merchantPackagesPage = 1
      this.merchantPackagesTotal = 0
      this.hasMoreMerchantPackages = false
      this.showPackageDropdown = false
    },
    async fetchMerchantPackagesByParking (parkingId, businessId, page = 1, append = false) {
      if (!businessId) {
        this.merchantPackages = []
        this.displayedMerchantPackages = []
        return
      }
      
      // 如果正在加载，不重复请求
      if (this.merchantPackagesLoading) {
        return
      }
      
      this.merchantPackagesLoading = true
      console.log('开始查询商家月卡套餐，parkingId=', parkingId, 'businessId=', businessId, 'page=', page)
      
      try {
        const res = await request({
          url: '/market/card/product/dropdown/list',
          method: 'post',
          data: {
            merchantId: businessId,
            parkingId: parkingId || null, // 如果选择了停车场，传入parkingId筛选
            page: page,
            size: this.merchantPackagesPageSize
          },
          timeout: 30000
        })
        
        const responseData = res?.data || {}
        const list = Array.isArray(responseData.data) ? responseData.data : []
        const total = responseData.total || 0
        
        console.log('查询到商家月卡套餐:', {
          page,
          size: list.length,
          total,
          list
        })
        
        // 转换为统一格式
        const packages = list.map(item => ({
          productId: item.productId,
          id: item.productId,
          productName: item.productName,
          name: item.productName,
          cardType: item.cardType,
          // 保留原始数据用于其他用途
          raw: item
        }))
        
        if (append) {
          // 追加模式：添加到现有列表
          this.merchantPackages = [...this.merchantPackages, ...packages]
        } else {
          // 重置模式：替换现有列表
          this.merchantPackages = packages
          this.merchantPackagesPage = 1
        }
        
        // 更新分页信息
        this.merchantPackagesTotal = total
        this.merchantPackagesPage = page
        this.hasMoreMerchantPackages = this.merchantPackages.length < total
        
        // 更新显示列表（根据停车场筛选）
        this.updateDisplayedMerchantPackages(parkingId)
        
        // 如果是第一页且有数据，默认选中第一个套餐
        if (!append && this.displayedMerchantPackages.length > 0) {
          this.selectPackage(this.displayedMerchantPackages[0])
        }
      } catch (e) {
        console.warn('查询商家月卡套餐失败:', e)
        if (!append) {
          this.merchantPackages = []
          this.displayedMerchantPackages = []
        }
      } finally {
        this.merchantPackagesLoading = false
      }
    },
    updateDisplayedMerchantPackages (parkingId) {
      // 根据选择的停车场筛选产品列表
      if (!parkingId) {
        // 如果没有选择停车场，显示所有产品
        this.displayedMerchantPackages = this.merchantPackages
      } else {
        // 筛选出包含该停车场的产品
        this.displayedMerchantPackages = this.merchantPackages.filter(pkg => {
          const raw = pkg.raw || {}
          const parkingList = raw.parkingList || []
          return parkingList.some(p => {
            const pid = p.parkingId || p.id
            return String(pid) === String(parkingId)
          })
        })
      }
    },
    handlePackageListScroll (event) {
      const target = event.target
      // 检查是否滚动到底部（距离底部50px内）
      const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight
      
      if (scrollBottom < 50 && this.hasMoreMerchantPackages && !this.merchantPackagesLoading) {
        // 加载下一页
        const nextPage = this.merchantPackagesPage + 1
        const parkingId = this.currentSelectedOption && this.currentSelectedOption.type === 'parking' 
          ? this.currentSelectedOption.value 
          : null
        this.fetchMerchantPackagesByParking(parkingId, this.currentBusinessId, nextPage, true)
      }
    },
    async selectPackage (pkg) {
      this.selectedMerchantPackage = pkg
      this.showPackageDropdown = false
      this.selectedMerchantPackageDetail = null // 先清空详情
      
      // 查询产品详情（必须等待完成，因为订单页面需要完整的产品信息）
      const productId = pkg?.productId || pkg?.id
      if (productId) {
        try {
          const detail = await this.fetchCardProductDetail(productId)
          this.selectedMerchantPackageDetail = detail
          console.log('查询到产品详情:', detail)
          
          // 将详情信息合并到 selectedMerchantPackage 中，确保数据完整
          if (detail) {
            this.selectedMerchantPackage = {
              ...pkg,
              ...detail,
              // 保留原始下拉列表数据
              raw: detail
            }
          }
        } catch (e) {
          console.warn('查询产品详情失败:', e)
          // 查询失败不影响主流程，继续执行，但会使用下拉列表中的简化数据
        }
      }
      
      // 加载该套餐的已购套餐（商家月卡订单）- 重置分页状态，从第一页开始
      if (this.currentBusinessId && productId) {
        console.log('[已购套餐] selectPackage: 准备加载已购套餐，重置分页状态:', {
          currentBusinessId: this.currentBusinessId,
          productId,
          beforeReset: {
            ownedPackagesPage: this.ownedPackagesPage,
            ownedPackagesTotal: this.ownedPackagesTotal,
            hasMoreOwnedPackages: this.hasMoreOwnedPackages,
            ownedPackagesLength: this.ownedPackages.length
          }
        })
        this.ownedPackagesPage = 1
        this.ownedPackages = []
        // 重置车牌总数
        this.totalOrderPlateNumbers = null
        console.log('[已购套餐] selectPackage: 调用fetchOwnedPackagesByBusiness，参数:', {
          businessId: this.currentBusinessId,
          productId,
          page: 1,
          append: false
        })
        this.fetchOwnedPackagesByBusiness(this.currentBusinessId, productId, 1, false)
        // 同时查询所有订单的车牌总数
        this.fetchTotalOrderPlateNumbers(this.currentBusinessId, productId)
      }
      // 重新加载在场车辆列表（只显示该套餐的车辆）
      if (this.currentSelectedOption && this.currentSelectedOption.type === 'parking') {
        const name = this.currentSelectedOption.label && this.currentSelectedOption.label.split(' - ')[0]
        this.fetchInParkByOrder({ parkingId: this.currentSelectedOption.value, parkingName: name })
      } else if (this.currentBusinessId) {
        this.fetchInParkByOrder({})
      }
    },
    async fetchCardProductDetail (productId) {
      // 根据产品ID查询产品详情
      if (!productId) {
        console.warn('fetchCardProductDetail: productId为空')
        return null
      }
      
      console.log('开始查询产品详情，productId=', productId)
      
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      
      try {
        const resp = await tryRequest([
          { url: '/market/card/product/get', method: 'get', params: { productId }, timeout: 30000 },
          { url: 'http://localhost:8100/market/card/product/get', method: 'get', params: { productId }, timeout: 30000 },
          { url: 'http://localhost:8019/market/card/product/get', method: 'get', params: { productId }, timeout: 30000 }
        ])
        
        const detail = resp?.data || null
        console.log('查询产品详情成功:', detail)
        return detail
      } catch (e) {
        console.error('查询产品详情失败:', e)
        throw e
      }
    },
    togglePackageDropdown () {
      this.showPackageDropdown = !this.showPackageDropdown
      if (this.showPackageDropdown) {
        this.$nextTick(() => {
          if (this.$refs.packageSearchInput) {
            this.$refs.packageSearchInput.focus()
          }
        })
      }
    },
    handlePackageOutsideClick (evt) {
      const root = this.$el.querySelector('.select-package')
      if (root && !root.contains(evt.target)) {
        this.showPackageDropdown = false
      }
    },
    async fetchPackagesByParking (parkingId) {
      console.log('开始查询套餐，parkingId=', parkingId)
      try {
        const res = await request({
          url: '/market/card/product/manage/search',
          method: 'post',
          data: { 
            parkingId,
            isSupportSellToPublic: null
          },
          timeout: 30000 // 设置30秒超时
        })
        const list = (res && (res.data?.records || res.data || [])) || []
        this.packages = Array.isArray(list) ? list : []
        console.log('已购套餐(按parkingId查询):', this.packages)
      } catch (e) {
        this.packages = []
      }
    },
    async fetchPackagesByRoad (roadId) {
      console.log('开始查询套餐，roadId=', roadId)
      try {
        const res = await request({
          url: '/market/card/product/manage/search',
          method: 'post',
          data: { 
            roadId,
            isSupportSellToPublic: null
          },
          timeout: 30000 // 设置30秒超时
        })
        const list = (res && (res.data?.records || res.data || [])) || []
        this.packages = Array.isArray(list) ? list : []
        console.log('已购套餐(按roadId查询):', this.packages)
      } catch (e) {
        this.packages = []
      }
    },
    async fetchPackagesByBusiness (businessId) {
      if (!businessId) { this.packages = []; return }
      console.log('开始查询套餐，businessId=', businessId)
      try {
        const res = await request({
          url: '/market/card/product/search',
          method: 'post',
          data: { 
            // 后端使用 merchantId/merchantIds 字段
            merchantId: businessId,
            isSupportSellToPublic: null
          },
          timeout: 30000
        })
        const list = (res && (res.data?.records || res.data || [])) || []
        this.packages = Array.isArray(list) ? list : []
        console.log('已购套餐(按businessId查询):', this.packages)
      } catch (e) {
        this.packages = []
      }
    },
    async fetchOwnedPackagesByBusiness (businessId, productId = null, page = 1, append = false) {
      // 分页查询已购套餐（订单）
      // 如果提供了productId，只显示该套餐的订单
      if (!businessId) { 
        this.ownedPackages = []
        this.ownedPackagesPage = 1
        this.ownedPackagesTotal = 0
        this.hasMoreOwnedPackages = false
        return 
      }
      
      // 如果正在加载，不重复请求
      if (this.ownedPackagesLoading) {
        return
      }
      
      this.ownedPackagesLoading = true
      console.log('[已购套餐] fetchOwnedPackagesByBusiness: 开始查询已购套餐，参数:', { 
        businessId, 
        productId, 
        page,
        append,
        pageSize: this.ownedPackagesPageSize,
        currentState: {
          ownedPackagesPage: this.ownedPackagesPage,
          ownedPackagesTotal: this.ownedPackagesTotal,
          hasMoreOwnedPackages: this.hasMoreOwnedPackages,
          ownedPackagesLength: this.ownedPackages.length,
          ownedPackagesLoading: this.ownedPackagesLoading
        }
      })
      
      try {
        // 分页查询订单
        const ordersResult = await this.fetchMonthlyCardOrdersByBusinessPage(businessId, productId, page, this.ownedPackagesPageSize)
        const orders = ordersResult.orders || []
        const total = ordersResult.total
        const hasMoreData = ordersResult.hasMoreData || false // 如果当前页返回的数量等于请求的size，说明可能还有更多数据
        
        console.log('查询到已购套餐订单:', {
          page,
          size: orders.length,
          total,
          hasMoreData,
          orders
        })
        
        // 如果是追加模式，需要去重，避免重复添加相同的订单
        if (append && this.ownedPackages.length > 0) {
          const existingOrderIds = new Set(this.ownedPackages.map(pkg => String(pkg.orderId)))
          const uniqueOrders = orders.filter(order => {
            const orderId = order?.orderId || order?.id || order?.order_id
            return orderId && !existingOrderIds.has(String(orderId))
          })
          
          if (uniqueOrders.length < orders.length) {
            console.log('过滤重复订单:', {
              original: orders.length,
              unique: uniqueOrders.length,
              filtered: orders.length - uniqueOrders.length
            })
          }
          
          // 使用去重后的订单列表
          orders.splice(0, orders.length, ...uniqueOrders)
        }
        
        if (!Array.isArray(orders) || orders.length === 0) {
          if (!append) {
            this.ownedPackages = []
          }
          // 如果接口返回了total，使用接口的total；否则保持之前的total或设置为0
          if (total != null && total !== undefined) {
            this.ownedPackagesTotal = total
          } else if (!append) {
            this.ownedPackagesTotal = 0
          }
          this.ownedPackagesPage = page
          this.hasMoreOwnedPackages = false
          return
        }
        
        // 获取所有订单涉及的产品ID，用于查询产品详情（补充车位数量等信息）
        const productIdSet = new Set()
        orders.forEach(order => {
          const orderProductId = order?.productId || order?.product_id
          if (orderProductId) productIdSet.add(orderProductId)
        })
        const productIdList = Array.from(productIdSet)
        
        // 查询产品信息，建立 productId -> productInfo 的映射
        let productInfoMap = {}
        if (productIdList.length > 0) {
          try {
            const productList = await this.fetchCardProductsByIds(productIdList)
            if (Array.isArray(productList)) {
              productList.forEach(product => {
                const pid = product?.productId || product?.id
                if (pid) productInfoMap[pid] = product
              })
            }
          } catch (e) {
            console.warn('[OwnedPackages] 查询产品信息失败，将仅显示订单信息:', e)
          }
        }
        
        // 将订单转换为显示格式，每个订单一个卡片
        const ownedList = orders.map(order => {
          const orderProductId = order?.productId || order?.product_id
          const productInfo = orderProductId ? productInfoMap[orderProductId] : null
          
          // 计算剩余天数
          let remainDays = null
          if (order?.cardValidEndTime || order?.card_valid_end_time) {
            try {
              const endDate = new Date(order.cardValidEndTime || order.card_valid_end_time)
              const now = new Date()
              const diff = endDate.getTime() - now.getTime()
              if (diff > 0) {
                remainDays = Math.ceil(diff / (1000 * 60 * 60 * 24))
              } else {
                remainDays = 0
              }
            } catch (e) { /* ignore */ }
          }
          
          return {
            // 订单信息
            orderId: order?.orderId || order?.id || order?.order_id,
            // 产品信息（优先使用订单中的，如果没有则使用产品详情）
            productId: orderProductId,
            productName: order?.productName || order?.product_name || productInfo?.productName || productInfo?.name || '月卡套餐',
            // 有效期（从订单中获取）
            cardValidStartTime: order?.cardValidStartTime || order?.card_valid_start_time,
            cardValidEndTime: order?.cardValidEndTime || order?.card_valid_end_time,
            // 产品配置信息（从产品详情中获取）
            bindBerthNumbers: productInfo?.bindBerthNumbers || productInfo?.parkingSpace || null,
            // 剩余天数
            remainDays: remainDays,
            // 订单对象（用于计算已绑定车牌数）
            _order: order
          }
        })
        
        // 更新已购套餐列表
        if (append) {
          // 追加模式：添加到现有列表（已经去重过）
          this.ownedPackages = [...this.ownedPackages, ...ownedList]
        } else {
          // 重置模式：替换现有列表
          this.ownedPackages = ownedList
          this.ownedPackagesPage = 1
        }
        
        // 更新分页信息
        // 简化逻辑：
        // 1. 如果有total，使用 total 判断：当前数量 < total 表示还有更多
        // 2. 如果没有total，使用 hasMoreData 判断：返回数量 >= size 表示可能还有更多
        
        if (total != null && total !== undefined) {
          // 情况1：接口返回了total，使用total来判断
          this.ownedPackagesTotal = total
          // 追加模式：使用当前总数量；重置模式：使用本次返回的数量
          const currentCount = append ? this.ownedPackages.length : ownedList.length
          this.hasMoreOwnedPackages = currentCount < total
        } else {
          // 情况2：接口没有返回total，使用hasMoreData判断
          // hasMoreData = 返回数量 >= size，表示可能还有更多数据
          this.hasMoreOwnedPackages = hasMoreData
          
          // 如果没有更多数据，设置total为当前数量
          if (!this.hasMoreOwnedPackages) {
            this.ownedPackagesTotal = append ? this.ownedPackages.length : ownedList.length
          }
        }
        
        this.ownedPackagesPage = page
      } catch (e) {
        const status = e?.response?.status
        const data = e?.response?.data
        console.error('[OwnedPackages] 查询失败:', { message: e?.message, status, data, stack: e?.stack })
        if (!append) {
          this.ownedPackages = []
        }
        this.ownedPackagesTotal = 0
        this.hasMoreOwnedPackages = false
      } finally {
        this.ownedPackagesLoading = false
      }
    },
    async loadMoreOwnedPackages () {
      // 加载更多已购套餐
      console.log('[已购套餐] loadMoreOwnedPackages: 点击加载更多按钮，当前状态:', {
        ownedPackagesLoading: this.ownedPackagesLoading,
        hasMoreOwnedPackages: this.hasMoreOwnedPackages,
        ownedPackagesPage: this.ownedPackagesPage,
        ownedPackagesTotal: this.ownedPackagesTotal,
        ownedPackagesLength: this.ownedPackages.length,
        canLoadMore: !this.ownedPackagesLoading && this.hasMoreOwnedPackages
      })
      
      if (this.ownedPackagesLoading || !this.hasMoreOwnedPackages) {
        console.warn('[已购套餐] loadMoreOwnedPackages: 无法加载更多，原因:', {
          isLoading: this.ownedPackagesLoading,
          hasMore: this.hasMoreOwnedPackages,
          reason: this.ownedPackagesLoading ? '正在加载中' : '没有更多数据'
        })
        return
      }
      
      const nextPage = this.ownedPackagesPage + 1
      const productId = this.selectedMerchantPackage?.productId || this.selectedMerchantPackage?.id || null
      
      console.log('[已购套餐] loadMoreOwnedPackages: 开始加载更多，参数:', {
        nextPage,
        productId,
        currentBusinessId: this.currentBusinessId,
        currentPage: this.ownedPackagesPage,
        currentCount: this.ownedPackages.length
      })
      
      await this.fetchOwnedPackagesByBusiness(this.currentBusinessId, productId, nextPage, true)
    },
    async ensureBusinessContext (businessId, options = {}) {
      this.debugLog('ensureBusinessContext:start', { businessId, forceRefresh: options?.forceRefresh === true })
      if (!businessId) return null
      const forceRefresh = options?.forceRefresh === true
      if (!forceRefresh && this.businessContext && this.businessContext.businessId === businessId) {
        this.debugLog('ensureBusinessContext:hit-cache', { businessId })
        return this.businessContext
      }
      if (!forceRefresh && this.businessContextPromise && this.businessContextPromise.businessId === businessId) {
        this.debugLog('ensureBusinessContext:await-promise', { businessId })
        return this.businessContextPromise.promise
      }
      if (forceRefresh) {
        this.businessContext = null
        this.businessContextPromise = null
      }
      const fetchPromise = (async () => {
        const userIds = await this.fetchAdminUserIdsByBusiness(businessId)
        this.debugLog('ensureBusinessContext:userIds', { businessId, count: userIds?.length, userIds })
        if (!Array.isArray(userIds) || userIds.length === 0) {
          const empty = { businessId, userIds: [], orders: [], productIds: [], plateNumbers: [] }
          this.businessContext = empty
          this.debugLog('ensureBusinessContext:empty-userIds', { businessId })
          return empty
        }
        const ordersByUserId = await this.fetchMonthlyCardOrdersByUserIds(userIds)
        this.debugLog('ensureBusinessContext:orders-by-userId', { businessId, count: ordersByUserId?.length })
        
        // 通过 businessId 从关联表查询订单（解决云平台创建的订单查询不到的问题）
        const ordersByBusinessId = await this.fetchMonthlyCardOrdersByBusinessId(businessId)
        this.debugLog('ensureBusinessContext:orders-by-businessId', { businessId, count: ordersByBusinessId?.length })
        
        // 合并两个查询结果并去重
        const allOrders = [...(ordersByUserId || []), ...(ordersByBusinessId || [])]
        const seen = new Set()
        const orders = allOrders.filter(order => {
          const orderId = (order && (order.orderId || order.id || order.order_id)) || null
          if (!orderId) return true
          if (seen.has(orderId)) return false
          seen.add(orderId)
          return true
        })
        this.debugLog('ensureBusinessContext:orders-merged', { businessId, count: orders?.length, byUserId: ordersByUserId?.length, byBusinessId: ordersByBusinessId?.length })
        
        const productIds = this.extractProductIdsFromOrders(orders)
        const plateNumbers = this.extractPlateNumbersFromOrders(orders)
        this.debugLog('ensureBusinessContext:extracted', { businessId, productCount: productIds?.length, plateCount: plateNumbers?.length })
        const context = { businessId, userIds, orders, productIds, plateNumbers }
        this.debugLog('ensureBusinessContext:context', context)
        this.businessContext = context
        return context
      })()
      this.businessContextPromise = { businessId, promise: fetchPromise }
      try {
        return await fetchPromise
      } finally {
        if (this.businessContextPromise && this.businessContextPromise.businessId === businessId) {
          this.businessContextPromise = null
        }
        this.debugLog('ensureBusinessContext:resolved', { businessId })
      }
    },
    async fetchTotalOrderPlateNumbers (businessId, productId = null) {
      // 查询所有商家月卡订单中的车牌总数（去重后）
      // 使用 /market/business/card/order/search/plate-numbers 接口
      // 接口返回的 total 字段是订单总数，不是车牌号总数
      // 接口返回的 plateNumberCount 是当前页的车牌号数量（去重后）
      // 因此需要先获取订单总数，然后设置足够大的 size 一次性获取所有订单
      if (!businessId) {
        this.totalOrderPlateNumbers = null
        return
      }
      
      // 如果正在加载，不重复请求
      if (this.totalOrderPlateNumbersLoading) {
        return
      }
      
      this.totalOrderPlateNumbersLoading = true
      this.totalOrderPlateNumbers = null
      
      console.log('[车牌总数] 开始查询所有订单的车牌总数:', { businessId, productId })
      
      try {
        const base = process.env.NODE_ENV === 'development' ? 'http://localhost:8100' : ''
        
        // 第一步：先调用接口获取订单总数
        // 使用 /market/business/card/order/search/plate-numbers 接口，先获取第一页来得到 total（订单总数）
        const firstPageResp = await request({
          url: `${base}/market/business/card/order/search/plate-numbers`,
          method: 'get',
          params: {
            businessId: businessId,
            productId: productId || undefined,
            page: 1,
            size: 1 // 只需要获取订单总数，不需要获取车牌号
          },
          timeout: 30000
        })
        
        // 解析响应数据
        let firstPageResponseData = firstPageResp?.data || {}
        if (firstPageResponseData.data) {
          firstPageResponseData = firstPageResponseData.data
        }
        
        // total 字段是订单总数
        const orderTotal = firstPageResponseData.total || 0
        
        console.log('[车牌总数] 查询到订单总数:', orderTotal)
        
        if (orderTotal === 0) {
          this.totalOrderPlateNumbers = 0
          return
        }
        
        // 第二步：使用足够大的 size 一次性获取所有订单来统计车牌总数
        // 设置 size >= 订单总数，确保能一次性获取所有订单
        const plateNumbersResp = await request({
          url: `${base}/market/business/card/order/search/plate-numbers`,
          method: 'get',
          params: {
            businessId: businessId,
            productId: productId || undefined,
            page: 1,
            size: orderTotal // 设置等于订单总数，确保能获取所有订单
          },
          timeout: 60000
        })
        
        // 解析响应数据
        let responseData = plateNumbersResp?.data || {}
        if (responseData.data) {
          responseData = responseData.data
        }
        
        // plateNumberCount 就是所有订单的车牌号总数（去重后）
        const total = responseData.plateNumberCount || 0
        
        this.totalOrderPlateNumbers = total
        console.log('[车牌总数] 统计完成，所有订单的车牌总数（去重后）:', total)
      } catch (e) {
        console.warn('[车牌总数] 查询失败:', e)
        // 查询失败不影响主流程，使用已加载订单的统计
        this.totalOrderPlateNumbers = null
      } finally {
        this.totalOrderPlateNumbersLoading = false
      }
    },
    async fetchAdminUserIdsByBusiness (businessId) {
      // 从 marketing 获取该商户管理员 userId 列表
      this.debugLog('fetchAdminUserIdsByBusiness:start', { businessId })
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            // 非致命错误继续尝试
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      // 根据后端实现：GET /market/business/admin/list?businessId=...
      const basePorts = process.env.NODE_ENV === 'development' ? ['http://localhost:8100', 'http://localhost:8019', ''] : ['']
      const candidates = []
      basePorts.forEach(prefix => {
        candidates.push({ url: `${prefix}/market/business/admin/list`, method: 'get', params: { businessId }, timeout: 30000 })
        // 备选（某些网关仅接受POST+param）：
        candidates.push({ url: `${prefix}/market/business/admin/list`, method: 'post', params: { businessId }, timeout: 30000 })
      })
      const resp = await tryRequest(candidates)
      const rows = (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
      // 兼容多字段：userId / id
      const ids = rows.map(r => r.userId || r.id).filter(Boolean)
      // 去重
      const unique = Array.from(new Set(ids))
      this.debugLog('fetchAdminUserIdsByBusiness:done', { businessId, count: unique.length })
      return unique
    },
    async fetchMonthlyCardOrdersByUserIds (userIds) {
      // 从 marketing 分别按 userId 获取其月卡订单（B类），返回订单列表
      this.debugLog('fetchMonthlyCardOrdersByUserIds:start', { userIdsCount: userIds?.length })
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      const base = process.env.NODE_ENV === 'development' ? 'http://localhost:8100' : ''
      const orders = []
      for (const uid of (userIds || [])) {
        const payload = { userId: uid, orderType: 'B' }
        const resp = await tryRequest([
          { url: `${base}/market/card/order/search/page`, method: 'post', params: { page: 1, size: 50 }, data: payload, timeout: 60000 },
          { url: 'http://localhost:8019/market/card/order/search/page', method: 'post', params: { page: 1, size: 50 }, data: payload, timeout: 60000 },
          { url: '/market/card/order/search/page', method: 'post', params: { page: 1, size: 50 }, data: payload, timeout: 60000 }
        ])
        const rows = (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
        rows.forEach(r => {
          if (r) orders.push(r)
        })
        this.debugLog('fetchMonthlyCardOrdersByUserIds:per-user', { userId: uid, rows: rows?.length })
      }
      // 去重（按订单ID）
      const seen = new Set()
      const uniqueOrders = orders.filter(order => {
        const orderId = (order && (order.orderId || order.id || order.order_id)) || null
        if (!orderId) return true
        if (seen.has(orderId)) return false
        seen.add(orderId)
        return true
      })
      this.debugLog('fetchMonthlyCardOrdersByUserIds:done', { total: orders.length, unique: uniqueOrders.length })
      
      return uniqueOrders
    },
    async fetchMonthlyCardOrdersByBusinessId (businessId) {
      // 从 marketing 通过 businessId 从关联表查询月卡订单（B类），返回订单列表
      this.debugLog('fetchMonthlyCardOrdersByBusinessId:start', { businessId })
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      const base = process.env.NODE_ENV === 'development' ? 'http://localhost:8100' : ''
      try {
        const resp = await tryRequest([
          { url: `${base}/market/card/order/search/by-business`, method: 'get', params: { businessId, orderType: 'B' }, timeout: 60000 },
          { url: 'http://localhost:8019/market/card/order/search/by-business', method: 'get', params: { businessId, orderType: 'B' }, timeout: 60000 },
          { url: '/market/card/order/search/by-business', method: 'get', params: { businessId, orderType: 'B' }, timeout: 60000 }
        ])
        const orders = (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
        this.debugLog('fetchMonthlyCardOrdersByBusinessId:done', { businessId, count: orders?.length })
        return Array.isArray(orders) ? orders : []
      } catch (e) {
        this.debugLog('fetchMonthlyCardOrdersByBusinessId:error', { businessId, error: e?.message })
        // 如果查询失败，返回空数组，不影响主流程
        console.warn('通过businessId查询订单失败:', e)
        return []
      }
    },
    async fetchMonthlyCardOrdersByBusinessPage (businessId, productId = null, page = 1, size = 10) {
      // 分页查询商家月卡订单（B类），支持按产品ID筛选
      // 使用 /market/business/card/order/search/page 接口
      this.debugLog('fetchMonthlyCardOrdersByBusinessPage:start', { businessId, productId, page, size })
      
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      
      const base = process.env.NODE_ENV === 'development' ? 'http://localhost:8100' : ''
      
      try {
        // 构建请求参数
        // Query参数：page, size
        // Body参数：BusinessCardOrderQueryRequest { businessId, productId }
        const queryParams = {
          page: page,
          size: size
        }
        
        const bodyData = {
          businessId: businessId,
          orderType: 'B' // 确保查询商家月卡订单
        }
        
        // 如果提供了产品ID，添加到Body参数中
        if (productId) {
          bodyData.productId = productId
        }
        
        const resp = await tryRequest([
          { url: `${base}/market/business/card/order/search/page`, method: 'post', params: queryParams, data: bodyData, timeout: 60000 },
          { url: 'http://localhost:8019/market/business/card/order/search/page', method: 'post', params: queryParams, data: bodyData, timeout: 60000 },
          { url: '/market/business/card/order/search/page', method: 'post', params: queryParams, data: bodyData, timeout: 60000 }
        ])
        
        // 解析响应数据
        // 返回格式：MyApiResponse<List<BusinessCardOrderDTO>>
        // { code: 200, message: "成功", data: [...] }
        console.log('[fetchMonthlyCardOrdersByBusinessPage] 原始响应:', resp)
        const responseData = resp?.data || {}
        console.log('[fetchMonthlyCardOrdersByBusinessPage] responseData:', responseData)
        
        // 从 MyApiResponse 中提取订单列表
        // 响应格式可能是：{ code: 200, message: "成功", data: [...] }
        // 或者 HTTP 客户端可能已经解析了一层，所以可能是：{ data: [...] } 或直接是 [...]
        let orders = []
        if (Array.isArray(responseData)) {
          // 如果响应直接是数组
          orders = responseData
        } else if (Array.isArray(responseData.data)) {
          // 如果是 MyApiResponse 格式
          orders = responseData.data
        } else if (Array.isArray(responseData.records)) {
          // 兼容其他可能的格式
          orders = responseData.records
        } else if (Array.isArray(responseData.list)) {
          orders = responseData.list
        }
        
        console.log('[fetchMonthlyCardOrdersByBusinessPage] 解析后的订单列表:', orders)
        
        // 从响应中获取 total（如果接口支持返回总数）
        // 注意：根据接口实现，当提供 businessId 时，可能不支持返回 total
        // 需要通过返回的列表长度判断是否还有更多数据
        let total = responseData.total
        
        this.debugLog('fetchMonthlyCardOrdersByBusinessPage:done', { 
          businessId, 
          productId, 
          page, 
          size, 
          count: orders.length,
          total,
          responseData,
          orders 
        })
        
        // 如果当前页返回的数量 >= 请求的size，说明可能还有更多数据
        // 如果当前页返回的数量 < 请求的size，说明这是最后一页
        const hasMoreData = orders.length >= size
        
        return {
          orders: orders,
          total: total, // 可能为null或undefined
          page: page,
          size: size,
          hasMoreData: hasMoreData
        }
      } catch (e) {
        this.debugLog('fetchMonthlyCardOrdersByBusinessPage:error', { businessId, productId, page, error: e?.message })
        console.warn('分页查询商家月卡订单失败:', e)
        return {
          orders: [],
          total: 0,
          page: page,
          size: size,
          hasMoreData: false
        }
      }
    },
    extractProductIdsFromOrders (orders) {
      const result = new Set()
      ;(orders || []).forEach(order => {
        const id = order?.productId || order?.cardProductId || order?.cardId
        if (id) result.add(id)
      })
      return Array.from(result)
    },
    extractPlateNumbersFromOrders (orders) {
      const result = new Set()
      ;(orders || []).forEach(order => {
        const list = order?.plateNumbers || order?.plateNumberList || order?.plateList || order?.plates
        if (Array.isArray(list)) {
          list.forEach(plate => {
            const normalized = this.normalizePlateNumber(plate)
            if (normalized) result.add(normalized)
          })
        } else {
          const normalized = this.normalizePlateNumber(list)
          if (normalized) result.add(normalized)
        }
      })
      const arr = Array.from(result)
      this.debugLog('extractPlateNumbersFromOrders:result', { count: arr.length, plates: arr })
      
      return arr
    },
    normalizePlateNumber (plate) {
      if (!plate || typeof plate !== 'string') return ''
      return plate.trim().toUpperCase()
    },
    // 建立车牌号到月卡订单的映射关系
    buildPlateToOrderMap (orders, inParkPlates) {
      if (!Array.isArray(orders) || orders.length === 0) {
        this.debugLog('buildPlateToOrderMap:no-orders', { orders })
        return {}
      }
      if (!Array.isArray(inParkPlates) || inParkPlates.length === 0) {
        this.debugLog('buildPlateToOrderMap:no-plates', { inParkPlates })
        return {}
      }
      
      const map = {}
      // 标准化在场车辆的车牌号
      const inParkPlateSet = new Set(inParkPlates.map(p => {
        const plate = this.normalizePlateNumber(p.num || p)
        return plate
      }).filter(Boolean))
      
      this.debugLog('buildPlateToOrderMap:inParkPlateSet', {
        inParkPlates: Array.from(inParkPlateSet),
        originalPlates: inParkPlates.map(p => p.num || p)
      })
      
      // 遍历所有订单，找到包含在场车牌号的订单
      orders.forEach((order, orderIndex) => {
        // 尝试多种可能的车牌号字段名
        const orderPlates = order?.plateNumbers || order?.plateNumberList || order?.plateList || order?.plates || order?.plateNo || []
        const normalizedOrderPlates = Array.isArray(orderPlates) 
          ? orderPlates.map(p => this.normalizePlateNumber(p)).filter(Boolean)
          : [this.normalizePlateNumber(orderPlates)].filter(Boolean)
        
        this.debugLog(`buildPlateToOrderMap:order-${orderIndex}`, {
          orderId: order?.orderId || order?.id,
          rawPlates: orderPlates,
          normalizedPlates: normalizedOrderPlates
        })
        
        // 检查订单中的车牌号是否有在场车辆
        normalizedOrderPlates.forEach(plate => {
          if (inParkPlateSet.has(plate)) {
            // 如果该车牌号还没有映射，或者当前订单更相关，则更新映射
            if (!map[plate] || !map[plate].length) {
              map[plate] = []
            }
            // 避免重复添加同一个订单
            const orderId = order?.orderId || order?.id || order?.order_id
            const existing = map[plate].find(o => (o?.orderId || o?.id || o?.order_id) === orderId)
            if (!existing) {
              map[plate].push(order)
              this.debugLog(`buildPlateToOrderMap:matched`, {
                plate,
                orderId,
                orderIndex
              })
            }
          }
        })
      })
      
      this.debugLog('buildPlateToOrderMap:result', { 
        totalOrders: orders.length, 
        inParkPlates: inParkPlates.length,
        inParkPlateSetSize: inParkPlateSet.size,
        mappedPlates: Object.keys(map).length,
        mappedPlatesList: Object.keys(map),
        map 
      })
      
      // 打印详细调试信息
      if (this.shouldDebug()) {
        console.log('[Home] buildPlateToOrderMap 详细结果:', {
          totalOrders: orders.length,
          inParkPlatesCount: inParkPlates.length,
          inParkPlateSet: Array.from(inParkPlateSet),
          mappedPlates: Object.keys(map),
          mapDetails: Object.keys(map).map(plate => ({
            plate,
            orders: map[plate].map(o => ({
              orderId: o?.orderId || o?.id,
              cardValidStartTime: o?.cardValidStartTime || o?.card_valid_start_time,
              cardValidEndTime: o?.cardValidEndTime || o?.card_valid_end_time
            }))
          }))
        })
      }
      
      return map
    },
    formatEntryTime (raw) {
      // 格式化时间，加上8小时（UTC+8时区）
      if (!raw) return ''
      try {
        const d = new Date(raw)
        if (isNaN(d.getTime())) {
          return String(raw).slice(0, 16).replace('T', ' ')
        }
        // 加上8小时（8 * 60 * 60 * 1000 毫秒）
        const dPlus8 = new Date(d.getTime() + 8 * 60 * 60 * 1000)
        return dPlus8.toISOString().slice(0, 16).replace('T', ' ')
      } catch (e) {
        return String(raw).slice(0, 16).replace('T', ' ')
      }
    },
    async queryPlateStatuses (plateNumbers, { parkingId, parkingName } = {}) {
      if (!Array.isArray(plateNumbers) || plateNumbers.length === 0) return []
      const uniquePlates = Array.from(new Set(plateNumbers.map(p => this.normalizePlateNumber(p)).filter(Boolean)))
      this.debugLog('queryPlateStatuses:start', { total: plateNumbers.length, unique: uniquePlates.length, parkingId, parkingName })
      if (uniquePlates.length === 0) return []
      // 使用精准搜索接口：/order/parking-record/in-order/search
      const orderUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8086/order/parking-record/in-order/search'
        : '/order/parking-record/in-order/search'
      const results = []
      for (const plate of uniquePlates) {
        // 按照正确的接口参数格式构建请求体
        const payload = {
          plateNumber: plate,           // 车牌号参数名
          hasEntryTime: true,           // 需要返回入场时间
          hasExitTime: false,           // 不需要返回出场时间
          hasPlateNumber: true,         // 需要返回车牌号
          includeAllStatus: true,      // 包含所有状态（该接口会返回在场记录）
          pageNo: 1,                   // 页码
          pageSize: 1                  // 每页数量
        }
        if (parkingId) payload.parkingId = parkingId
        else if (parkingName) payload.parkingName = parkingName
        this.debugLog('queryPlateStatuses:request', { plate, payload })
        try {
          const resp = await request({
            url: orderUrl,
            method: 'post',
            data: payload,
            timeout: 30000
          })
          const rows = (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
          this.debugLog('queryPlateStatuses:plate-response', { plate, rows: rows?.length })
          if (!Array.isArray(rows) || rows.length === 0) continue
          const row = rows[0]
          // 判断是否为在场记录：accessType 为 "ENTRY" 表示入场记录（在场）
          const accessType = row?.accessType || row?.access_type
          // 如果 accessType 不是 "ENTRY"，说明不是入场记录，跳过
          if (accessType && String(accessType).toUpperCase() !== 'ENTRY') continue
          // 兼容其他状态判断方式
          const status = (row?.status || row?.orderStatus || row?.recordStatus || 'IN')
          if (status && String(status).toUpperCase() !== 'IN') continue
          const id = row?.orderId || row?.id || row?.order_id || row?.recordId || row?.inId
          const rawPlate = row?.plateNumber || row?.plate_no || row?.plateNo || plate
          // 优先使用 entryTime 字段（根据图片中的返回数据）
          const timeRaw = (
            row?.entryTime || row?.entry_time || row?.inTime || row?.in_time || 
            row?.inDatetime || row?.in_datetime || row?.inUtcTime || row?.inUtcEnterTime || 
            row?.enterTime || row?.enterTime || row?.utcIn || row?.inDateTime || 
            row?.inParkTime || row?.gmtEntry || row?.enter_datetime
          )
          results.push({
            id,
            num: this.normalizePlateNumber(rawPlate),
            time: this.formatEntryTime(timeRaw),
            status: status || 'IN',
            raw: row
          })
        } catch (e) {
          this.debugLog('queryPlateStatuses:error', { plate, message: e?.message })
          if (this.shouldDebug()) {
            // eslint-disable-next-line no-console
            console.warn('[PlateStatus] 查询失败:', plate, e)
          }
        }
      }
      this.debugLog('queryPlateStatuses:done', { matched: results.length })
      
      return results
    },
    async fetchCardProductsByIds (productIds) {
      // 根据产品ID列表批量查询产品详情（marketing-卡产品管理）
      const uniqueIds = Array.from(new Set(productIds))
      if (uniqueIds.length === 0) return []
      const tryRequest = async (candidates) => {
        let lastErr
        for (const cfg of candidates) {
          try {
            const resp = await request(cfg)
            return resp
          } catch (e) {
            lastErr = e
            if (e?.response?.status && ![404, 500].includes(e.response.status)) throw e
          }
        }
        if (lastErr) throw lastErr
      }
      const resp = await tryRequest([
        // 优先：月卡套餐管理
        { url: '/market/card/product/search', method: 'post', data: { productIds: uniqueIds }, timeout: 30000 },
        ])
      return (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
    },
    async fetchInParkByOrder ({ parkingId, parkingName, page = 1, append = false } = {}) {
      this.debugLog('fetchInParkByOrder:start', { parkingId, parkingName, page, append, currentBusinessId: this.currentBusinessId, selectedPackage: this.selectedMerchantPackage })
      
      // 获取必要的参数
      const productId = this.selectedMerchantPackage?.productId || this.selectedMerchantPackage?.id
      const finalParkingId = parkingId || (this.currentSelectedOption?.type === 'parking' ? this.currentSelectedOption.value : null)
      const merchantId = this.currentBusinessId
      
      // 检查必填参数
      if (!productId || !finalParkingId) {
        this.debugLog('fetchInParkByOrder:missing-params', { productId, parkingId: finalParkingId, merchantId })
        if (!append) {
          this.plateRawRows = []
          this.plateDetails = []
          this.plates = []
        }
        return
      }
      
      // 如果正在加载，不重复请求
      if (this.inParkVehiclesLoading) {
        return
      }
      
      this.inParkVehiclesLoading = true
      
      try {
        // 后端接口不再分页：一次性返回所有在场车辆，前端做分页展示
        console.log('开始查询在场车辆（后端不分页，前端切片展示），参数:', { productId, parkingId: finalParkingId, merchantId })
        
        // 注意：request.js 响应拦截器会直接返回 response.data（也就是 MyApiResponse 本体）
        const res = await request({
          url: '/market/merchant-card-vehicle/list',
          method: 'post',
          data: {
            productId,
            parkingId: finalParkingId,
            merchantId: merchantId || null, // 可选参数
            onlyInPark: true // 只查询在场车辆
          },
          timeout: 30000
        })
        
        // 后端返回：{ code, message, data: { parkCarList: [], roadCarList: [] } }
        const responseData = res?.data || {}
        const parkCarList = Array.isArray(responseData.parkCarList) ? responseData.parkCarList : []
        const roadCarList = Array.isArray(responseData.roadCarList) ? responseData.roadCarList : []
        // 首页当前是停车场场景，优先使用 parkCarList；兜底使用 roadCarList
        const allVehicles = parkCarList.length > 0 ? parkCarList : roadCarList
        const total = allVehicles.length
        
        console.log('查询到在场车辆:', {
          total,
          count: allVehicles.length,
          list: allVehicles
        })
        
        // 保存全量数据，并按页大小切片生成当前展示数据
        this.inParkVehiclesAll = allVehicles
        this.inParkVehiclesTotal = total || 0
        this.inParkVehiclesPage = 1

        const visibleVehicles = allVehicles.slice(0, this.inParkVehiclesPageSize)

        // 转换为页面显示格式（当前页展示）
        const newPlateDetails = visibleVehicles.map((vehicle, index) => ({
          id: `vehicle-1-${index}-${vehicle.plateNumber}`,
          num: this.normalizePlateNumber(vehicle.plateNumber),
          time: this.formatEntryTime(vehicle.entryTime),
          status: 'IN'
        }))
        
        const newPlateNums = newPlateDetails.map(item => item.num).filter(Boolean)
        
        // 重置模式：替换现有列表（后端不分页，append/page 参数仅为兼容旧调用）
        this.plateRawRows = visibleVehicles
        this.plateDetails = newPlateDetails
        this.plates = newPlateNums

        // 判断是否还有更多数据（前端分页）
        this.hasMoreInParkVehicles = this.plateDetails.length < this.inParkVehiclesTotal
        
        console.log('[在场车辆分页] 更新状态:', {
          page: this.inParkVehiclesPage,
          pageSize: this.inParkVehiclesPageSize,
          allVehiclesLength: allVehicles.length,
          totalPlateDetailsLength: this.plateDetails.length,
          total: this.inParkVehiclesTotal,
          hasMoreInParkVehicles: this.hasMoreInParkVehicles
        })
        
        // 清空订单映射（新接口直接返回在场车辆，不需要建立映射）
        this.plateToOrderMap = {}
        
        this.debugLog('fetchInParkByOrder:done', { plateDetailsCount: this.plateDetails.length, plateDetails: this.plateDetails })
        
        // 保存到 sessionStorage（保持兼容性）
        try {
          sessionStorage.setItem('latest_plate_payload', JSON.stringify({
            timestamp: Date.now(),
            plateDetails: this.plateDetails,
            plates: this.plates,
            inParkRows: this.plateRawRows,
            businessId: merchantId
          }))
        } catch (e) { /* ignore */ }
      } catch (e) {
        if (this.shouldDebug()) {
          // eslint-disable-next-line no-console
          console.error('加载在场车辆失败:', e)
        }
        this.debugLog('fetchInParkByOrder:error', { message: e?.message })
        if (!append) {
          this.plateRawRows = []
          this.plateDetails = []
          this.plates = []
        }
      } finally {
        this.inParkVehiclesLoading = false
      }
    },
    async loadMoreInParkVehicles () {
      // 加载更多在场车辆
      if (this.inParkVehiclesLoading || !this.hasMoreInParkVehicles) {
        return
      }
      
      // 前端分页：从全量列表中切片追加到展示列表
      const nextPage = (this.inParkVehiclesPage || 1) + 1
      const end = nextPage * this.inParkVehiclesPageSize
      const visibleVehicles = (this.inParkVehiclesAll || []).slice(0, end)

      console.log('[在场车辆] loadMoreInParkVehicles: 前端切片加载更多:', {
        nextPage,
        end,
        total: this.inParkVehiclesTotal,
        currentVisible: visibleVehicles.length
      })

      const newPlateDetails = visibleVehicles.map((vehicle, index) => ({
        id: `vehicle-${nextPage}-${index}-${vehicle.plateNumber}`,
        num: this.normalizePlateNumber(vehicle.plateNumber),
        time: this.formatEntryTime(vehicle.entryTime),
        status: 'IN'
      }))
      const newPlateNums = newPlateDetails.map(item => item.num).filter(Boolean)

      this.inParkVehiclesPage = nextPage
      this.plateRawRows = visibleVehicles
      this.plateDetails = newPlateDetails
      this.plates = newPlateNums
      this.hasMoreInParkVehicles = this.plateDetails.length < this.inParkVehiclesTotal
    },
    async fetchParkingInfoById (parkingId) {
      this.totalBerths = null
      try {
        const tryRequest = async (candidates) => {
          let lastErr
          for (const cfg of candidates) {
            try {
              return await request(cfg)
            } catch (e) {
              lastErr = e
              if (e?.response?.status !== 404 && e?.response?.status !== 500) throw e
            }
          }
          if (lastErr) throw lastErr
        }
        const resp = await tryRequest([
          { url: '/parking/info/search', method: 'post', params: { page: 1, size: 1 }, data: { parkingId } },
          { url: '/market/parking/info/search', method: 'post', params: { page: 1, size: 1 }, data: { parkingId } }
        ])
        const list = (resp && (resp.data?.records || resp.data || [])) || []
        const info = Array.isArray(list) ? list[0] : null
        const total = info && (info.totalBerths || info.totalParkingSpace || info.totalBerth || info.total || info.berthCount || info.totalBerthNumbers || info.actualBerths)
        this.totalBerths = (typeof total === 'number') ? total : (Number(total) || null)
      } catch (e) {
        this.totalBerths = null
      }
    },
    isDevEnv () {
      return typeof process !== 'undefined' && process && process.env && process.env.NODE_ENV === 'development'
    },
    shouldDebug () {
      return !!this.debugEnabled || this.isDevEnv()
    },
    debugLog (label, payload) {
      if (!this.shouldDebug()) return
      try {
        // eslint-disable-next-line no-console
        console.log(`[HomeDebug] ${label}`, payload)
      } catch (e) { /* ignore */ }
    },
    formatDate (v) {
      if (!v) return ''
      try {
        const d = new Date(v)
        if (!isNaN(d.getTime())) return `${d.getFullYear()}年${('0'+(d.getMonth()+1)).slice(-2)}月${('0'+d.getDate()).slice(-2)}日`
        return String(v).slice(0, 10)
      } catch (e) { return '' }
    },
    // 获取订单的实际已绑定车牌数（从订单的plateNumbers数组中统计，过滤空值）
    getOrderBindPlateCount (pkg) {
      if (!pkg) return '-'
      
      // 优先从订单对象中获取车牌号列表
      const order = pkg._order
      if (order) {
        const plates = order?.plateNumbers || 
                       order?.plateNumberList || 
                       order?.plateList || 
                       order?.plates || []
        
        if (Array.isArray(plates)) {
          // 过滤掉空字符串、null、undefined等无效值，只统计有效的车牌号
          const validPlates = plates.filter(p => {
            if (p == null) return false // 过滤 null 和 undefined
            const str = String(p).trim()
            return str.length > 0 // 过滤空字符串
          })
          return validPlates.length
        }
      }
      
      // 如果没有订单对象，回退到产品配置字段
      return pkg.maxBindPlateNumbers || pkg.plateNumberCount || 0
    }
  }
}
</script>

<style scoped>
.main-page {
  width: 100vw;
  max-width: 430px;
  margin: 0 auto;
  background: #f7f8fa;
  min-height: 100vh;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.main-header {
  font-size: 22px;
  font-weight: bold;
  color: #222;
  margin: 0 0 18px 0;
  padding: 24px 0 0 24px;
}

.select-lot {
  margin-left: auto !important;
  margin-right: auto !important;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80%;
  max-width: 360px;
  height: 48px;
  border-radius: 12px;
  border: 1.5px solid #eee;
  background: #fff;
  font-size: 20px;
  color: #888;
  padding: 0 16px;
  outline: none;
  position: relative;
}

.select-lot .lot-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 18px;
  color: #222;
}

.select-lot .lot-input::placeholder {
  color: #888;
}

.select-lot .caret {
  font-size: 20px;
  color: #bbb;
}

.select-package {
  margin-left: auto !important;
  margin-right: auto !important;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80%;
  max-width: 360px;
  height: 48px;
  border-radius: 12px;
  border: 1.5px solid #eee;
  background: #fff;
  font-size: 20px;
  color: #888;
  padding: 0 16px;
  outline: none;
  position: relative;
  margin-top: 12px;
}

.select-package .package-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 18px;
  color: #222;
  cursor: pointer;
}

.select-package .package-input::placeholder {
  color: #888;
}

.select-package .package-dropdown-panel {
  position: absolute;
  top: 52px;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 6px 18px #00000014;
  padding: 8px 0 8px 0;
  z-index: 20;
  max-height: 320px;
  overflow: hidden;
}

.clear-selection-btn {
  background: transparent;
  border: none;
  color: #999;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  padding: 0 8px;
  margin-right: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.clear-selection-btn:hover {
  background: #f0f0f0;
  color: #666;
}

.select-lot .dropdown-panel {
  position: absolute;
  top: 52px;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 6px 18px #00000014;
  padding: 8px 0 8px 0;
  z-index: 20;
  max-height: 320px;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: 14px;
  color: #666;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

.clear-btn {
  background: transparent;
  border: none;
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
}

.dropdown-list {
  max-height: 260px;
  overflow: auto;
  padding: 4px 8px 8px 8px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.dropdown-item:hover {
  background: #f7f8fa;
}

.dropdown-item .tag {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 8px;
  color: #fff;
}

.dropdown-item .tag.parking { background: #4ca1ff; }
.dropdown-item .tag.road { background: #09bb07; }

.dropdown-item .label {
  font-size: 15px;
  color: #222;
}

.dropdown-empty, .dropdown-loading, .dropdown-load-more {
  text-align: center;
  color: #999;
  padding: 10px 0;
  font-size: 14px;
}

.dropdown-load-more {
  color: #1890ff;
  cursor: pointer;
}

.package-card {
  width: calc(100% - 48px);
  margin: 0 24px 18px 24px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  padding: 24px 0 24px 0;
  text-align: center;
  margin-top: 24px !important;
}

.package-title {
  font-size: 24px;
  font-weight: bold;
  color: #222;
  margin-bottom: 12px;
}

.package-price {
  font-size: 28px;
  font-weight: bold;
  color: #ff9900;
  margin-bottom: 18px;
}

.no-package-text {
  font-size: 18px;
  color: #999;
  padding: 24px 0;
}

.buy-btn, .manage-btn {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  height: 48px;
  line-height: 48px;
  padding: 0;
  margin-left: auto;
  margin-right: auto;
  display: block;
  background: linear-gradient(90deg,#4ca1ff,#70e1f5);
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  border: none;
  border-radius: 16px;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  box-shadow: 0 2px 8px #4ca1ff33;
}

.buy-btn:active, .manage-btn:active {
  background: #4ca1ff;
  transform: scale(0.98);
}

.owned-title {
  font-size: 18px;
  color: #888;
  margin: 24px 0 0 24px;
  font-weight: 500;
}

.owned-card {
  margin-left: auto;
  margin-right: auto;
  width: calc(100% - 56px);
  margin-top: 12px;
  margin-bottom: 18px;
  border-radius: 18px;
  background: linear-gradient(90deg,#b2f7ef,#e0f7fa 80%);
  padding: 16px 14px 16px 14px;
  color: #222;
  position: relative;
}

.owned-card .remain {
  font-size: 13px !important;
  right: 12px !important;
  top: 12px !important;
}

.owned-card .pkg {
  font-size: 17px !important;
}

.owned-card .valid, .owned-card .info {
  font-size: 13px !important;
}

.load-more-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 12px 0 0;
}

.load-more-btn {
  min-width: 140px;
  height: 36px;
  border: none;
  border-radius: 18px;
  background: transparent;
  color: #3572e6;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.load-more-loading {
  text-align: center;
  color: #999;
  padding: 16px 0;
  font-size: 14px;
}

.no-more-text {
  color: #999;
  font-size: 13px;
  padding: 6px 0;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #222;
  margin: 24px 0 8px 24px;
}

.plate-list-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px #e0e0e033;
  margin: 0 0 18px;
  overflow: hidden;
}

.inlot-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  color: #222;
  padding: 0 18px;
  min-height: 38px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.plate-list {
  margin-left: auto !important;
  margin-right: auto !important;
  width: calc(100% - 24px);
  border-radius: 0 0 12px 12px;
  padding: 0 12px 0 12px;
  font-size: 17px;
  color: #222;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 自定义滚动条样式 */
.plate-list::-webkit-scrollbar {
  width: 6px;
}

.plate-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.plate-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.plate-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}


.plate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  font-size: 18px;
  background: #fff;
  margin-bottom: 8px;
  border-radius: 12px;
  box-shadow: 0 2px 8px #e0e0e033;
  transition: background 0.2s, box-shadow 0.2s;
  border: none;
  position: relative;
  min-height: unset;
  height: auto;
}

.plate-row:hover {
  background: #f7f8fa;
  box-shadow: 0 4px 16px #238aff22;
}

.plate-row .plate-icon {
  font-size: 22px;
  color: #3572e6;
  margin-right: 12px;
}

.plate-row .plate-info-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
}

.plate-row .plate-number {
  font-weight: bold;
  color: #222;
  font-size: 18px;
}

.plate-row .plate-time {
  font-size: 13px;
  color: #888;
  font-weight: normal;
}

.plate-row .plate-status {
  background: #e6ffe6;
  color: #09bb07;
  font-size: 15px;
  border-radius: 10px;
  padding: 2px 12px;
  margin: 0 8px;
  font-weight: 500;
}

.plate-row .arrow {
  color: #bbb;
  font-size: 24px;
  margin-left: 8px;
}

@media (max-width: 430px) {
  .select-lot, .package-card, .owned-card, .inlot-bar, .plate-list { 
    margin-left: 8px; 
    margin-right: 8px; 
  }
}
</style>
